import { LockKeyhole, LogIn, UserRound, X } from "lucide-react";
import { FormEvent, useState } from "react";
import { messageOf } from "../ui/format";
import { type AuthMode } from "../ui/navigation";

export function AuthDialog({
  mode,
  guest,
  username: currentUsername,
  nickname: currentNickname,
  onMode,
  onClose,
  onBind,
  onLogin,
  onNickname,
  onPassword,
  onDelete,
  onLogout,
}: {
  mode: AuthMode;
  guest: boolean;
  username?: string;
  nickname: string;
  onMode: (mode: AuthMode) => void;
  onClose: () => void;
  onBind: (
    username: string,
    password: string,
    passwordConfirm: string,
    nickname: string,
  ) => Promise<void>;
  onLogin: (username: string, password: string) => Promise<void>;
  onNickname: (nickname: string) => Promise<void>;
  onPassword: (currentPassword: string, password: string, passwordConfirm: string) => Promise<void>;
  onDelete: () => Promise<void>;
  onLogout: () => Promise<void>;
}) {
  const [username, setUsername] = useState("");
  const [nickname, setNickname] = useState(currentNickname);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [accountSection, setAccountSection] = useState<"nickname" | "password">("nickname");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (mode === "bind" && password !== passwordConfirm) {
      setError("两次输入的密码不一致");
      return;
    }
    setPending(true);
    setError("");
    try {
      if (mode === "bind") await onBind(username, password, passwordConfirm, nickname);
      else await onLogin(username, password);
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setPending(false);
    }
  };
  const submitNickname = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError("");
    setSuccess("");
    try {
      await onNickname(nickname);
      setSuccess("昵称已更新");
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setPending(false);
    }
  };
  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== passwordConfirm) {
      setError("两次输入的新密码不一致");
      return;
    }
    setPending(true);
    setError("");
    setSuccess("");
    try {
      await onPassword(currentPassword, password, passwordConfirm);
      setCurrentPassword("");
      setPassword("");
      setPasswordConfirm("");
      setSuccess("密码已更新");
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setPending(false);
    }
  };
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section className="auth-dialog" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        <button className="icon-button close-modal" onClick={onClose} aria-label="关闭">
          <X size={18} />
        </button>
        <p className="eyebrow">ACCOUNT</p>
        <h2 id="auth-title">
          {guest ? (mode === "bind" ? "注册并保留进度" : "登录已有账号") : "账号管理"}
        </h2>
        {guest && mode === "bind" && <p>注册后可在其他设备继续当前进度。</p>}
        {guest ? (
          <>
            <form onSubmit={submit}>
              <label>
                用户名
                <input
                  type="text"
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoComplete="username"
                  minLength={3}
                  maxLength={24}
                  pattern="[A-Za-z0-9_]{3,24}"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  required
                />
              </label>
              {mode === "bind" && (
                <label>
                  昵称
                  <input
                    type="text"
                    autoComplete="nickname"
                    minLength={2}
                    maxLength={16}
                    value={nickname}
                    onChange={(event) => setNickname(event.target.value)}
                    required
                  />
                </label>
              )}
              <label>
                密码
                <input
                  type="password"
                  minLength={10}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </label>
              {mode === "bind" && (
                <label>
                  确认密码
                  <input
                    type="password"
                    minLength={10}
                    autoComplete="new-password"
                    value={passwordConfirm}
                    onChange={(event) => setPasswordConfirm(event.target.value)}
                    required
                  />
                </label>
              )}
              {error && <div className="form-error">{error}</div>}
              <button className="primary-command compact" disabled={pending}>
                <LogIn size={18} />
                {pending ? "处理中" : mode === "bind" ? "注册账号" : "登录"}
              </button>
            </form>
            <div className="auth-switch">
              {mode === "bind" ? (
                <button type="button" onClick={() => onMode("login")}>
                  已有账号，切换登录
                </button>
              ) : (
                <button type="button" onClick={() => onMode("bind")}>
                  没有账号，注册并保留进度
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="account-summary">
              <strong>{currentNickname}</strong>
              <span>@{currentUsername}</span>
            </div>
            <div className="account-edit-tabs" role="tablist" aria-label="账号资料">
              <button
                type="button"
                role="tab"
                aria-selected={accountSection === "nickname"}
                onClick={() => {
                  setAccountSection("nickname");
                  setError("");
                  setSuccess("");
                }}
              >
                <UserRound size={15} />
                昵称
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={accountSection === "password"}
                onClick={() => {
                  setAccountSection("password");
                  setError("");
                  setSuccess("");
                }}
              >
                <LockKeyhole size={15} />
                密码
              </button>
            </div>
            {accountSection === "nickname" ? (
              <form onSubmit={submitNickname}>
                <label>
                  昵称
                  <input
                    type="text"
                    autoComplete="nickname"
                    minLength={2}
                    maxLength={16}
                    value={nickname}
                    onChange={(event) => setNickname(event.target.value)}
                    required
                  />
                </label>
                {error && (
                  <div className="form-error" role="alert">
                    {error}
                  </div>
                )}
                {success && (
                  <div className="form-success" role="status">
                    {success}
                  </div>
                )}
                <button className="primary-command compact" disabled={pending}>
                  <UserRound size={17} />
                  {pending ? "处理中" : "更新昵称"}
                </button>
              </form>
            ) : (
              <form onSubmit={submitPassword}>
                <label>
                  当前密码
                  <input
                    type="password"
                    minLength={10}
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    required
                  />
                </label>
                <label>
                  新密码
                  <input
                    type="password"
                    minLength={10}
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                </label>
                <label>
                  确认新密码
                  <input
                    type="password"
                    minLength={10}
                    autoComplete="new-password"
                    value={passwordConfirm}
                    onChange={(event) => setPasswordConfirm(event.target.value)}
                    required
                  />
                </label>
                {error && (
                  <div className="form-error" role="alert">
                    {error}
                  </div>
                )}
                {success && (
                  <div className="form-success" role="status">
                    {success}
                  </div>
                )}
                <button className="primary-command compact" disabled={pending}>
                  <LockKeyhole size={17} />
                  {pending ? "处理中" : "更新密码"}
                </button>
              </form>
            )}
            <div className="auth-switch">
              <button type="button" onClick={onLogout}>
                退出账号
              </button>
              <button type="button" className="danger-link" onClick={onDelete}>
                删除账号
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
